const APP_HOST = "app.lavado.in";

type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
  indexable: boolean;
};

const DEFAULT_SEO: SeoConfig = {
  title: "Lavado | Laundry Pickup, Dry Cleaning and Ironing",
  description:
    "Lavado offers hassle-free laundry pickup, dry cleaning and steam ironing with doorstep delivery.",
  keywords:
    "Lavado, laundry service, dry cleaning, steam iron, laundry pickup, doorstep laundry",
  indexable: false,
};

const PUBLIC_PAGE_SEO: Record<string, SeoConfig> = {
  "/": {
    title: "Lavado | Laundry Pickup, Dry Cleaning and Ironing",
    description:
      "Lavado offers hassle-free laundry pickup, dry cleaning and steam ironing with doorstep delivery.",
    keywords:
      "Lavado, laundry service, dry cleaning, steam iron, laundry pickup, doorstep laundry",
    indexable: true,
  },
  "/login": {
    title: "Lavado | Sign in to your laundry account",
    description:
      "Sign in to Lavado to book laundry pickup, track orders and manage your addresses.",
    keywords: "Lavado login, laundry app login, order tracking",
    indexable: true,
  },
  "/faq": {
    title: "Lavado FAQ | Laundry service questions answered",
    description:
      "Read frequently asked questions about Lavado services, pickup timings, pricing and order support.",
    keywords: "Lavado FAQ, laundry support, dry cleaning questions",
    indexable: false,
  },
  "/privacyPolicy": {
    title: "Lavado Privacy Policy",
    description:
      "Read how Lavado collects, uses and protects your personal data and order information.",
    keywords: "Lavado privacy policy, data protection, user privacy",
    indexable: false,
  },
  "/termsAndConditions": {
    title: "Lavado Terms and Conditions",
    description:
      "Review Lavado terms and conditions for using the platform, services and payment flows.",
    keywords: "Lavado terms, laundry app terms, service conditions",
    indexable: false,
  },
};

const APP_PATH_PREFIXES = [
  "/home",
  "/pricing",
  "/profile",
  "/orders",
  "/order",
  "/offers",
  "/help",
  "/address",
  "/editProfile",
  "/verify",
  "/complete_profile",
  "/splash",
];

function ensureMetaTagByName(name: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  return meta;
}

function ensureMetaTagByProperty(property: string) {
  let meta = document.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }
  return meta;
}

function ensureCanonicalLink() {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  return link;
}

function isProtectedAppPath(pathname: string) {
  return APP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function resolveSeo(pathname: string): SeoConfig {
  const pageSeo = PUBLIC_PAGE_SEO[pathname];

  if (pageSeo) {
    return pageSeo;
  }

  if (isProtectedAppPath(pathname)) {
    return DEFAULT_SEO;
  }

  return {
    ...DEFAULT_SEO,
    indexable: false,
  };
}

export function applySeo(pathname: string) {
  const host = window.location.hostname;
  const origin = host === APP_HOST ? window.location.origin : `https://${APP_HOST}`;
  const seo = resolveSeo(pathname);
  const canonicalUrl = `${origin}${pathname}`;

  document.title = seo.title;

  ensureMetaTagByName("description").setAttribute("content", seo.description);
  ensureMetaTagByName("keywords").setAttribute("content", seo.keywords);
  ensureMetaTagByName("robots").setAttribute(
    "content",
    seo.indexable ? "index, follow" : "noindex, nofollow",
  );

  ensureMetaTagByProperty("og:type").setAttribute("content", "website");
  ensureMetaTagByProperty("og:site_name").setAttribute("content", "Lavado");
  ensureMetaTagByProperty("og:title").setAttribute("content", seo.title);
  ensureMetaTagByProperty("og:description").setAttribute(
    "content",
    seo.description,
  );
  ensureMetaTagByProperty("og:url").setAttribute("content", canonicalUrl);

  ensureMetaTagByName("twitter:card").setAttribute("content", "summary_large_image");
  ensureMetaTagByName("twitter:title").setAttribute("content", seo.title);
  ensureMetaTagByName("twitter:description").setAttribute(
    "content",
    seo.description,
  );

  ensureCanonicalLink().setAttribute("href", canonicalUrl);
}
